import { Box, Typography } from "@mui/material";
import { makeStyles } from "components/providers/makeStyles";
import { T } from "components/util/t";
import get from "lodash/get";
import { DivisionBox } from "components/divisions/divisionBox";
import { AddDivision } from "components/divisions/AddDivision";
import { Control } from "react-hook-form";

interface DivisionsProps {
    divisions: any[];
    fields: any[];
    deleteDivision: (index: number) => void;
    divisionNameField: string;
    divisionIdField: string;
    divisionUuidField: string;
    RowRenderer: React.FCWithChildren<{ divisionId: any; uuid: any; index: number }>;
    disabled?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars, array-callback-return
const Divisions: React.FC<DivisionsProps> = ({ divisions, fields, deleteDivision, divisionNameField, divisionIdField, divisionUuidField, RowRenderer, disabled }) => fields.map((field, index) => {
    const division = divisions[index];

    if (division) return (
        <RowRenderer
            key={get(division, divisionIdField.split(".")) || get(division, divisionUuidField.split("."))}
            divisionId={get(division, divisionIdField.split("."))}
            uuid={get(division, divisionUuidField.split("."))}
            index={index}
        >
            <SelectedDivision name={get(division, divisionNameField.split("."))} index={index} deleteDivision={deleteDivision} disabled={disabled} />
        </RowRenderer>
    );
});

const useSelectedDivisionStyles = makeStyles(() => ({
    division: {
        "&&": {
            width: "100%"
        }
    }
}));

interface SelectedDivisionProps {
    name: string;
    index: number;
    deleteDivision: (index: number) => void;
    fullWidth?: boolean;
    disabled?: boolean;
}

export const SelectedDivision: React.FC<SelectedDivisionProps> = ({ name, index, deleteDivision, fullWidth, disabled }) => {
    const classes = useSelectedDivisionStyles();

    return <DivisionBox name={name} onRemove={() => deleteDivision(index)} fullWidth={fullWidth} className={classes.division} disabled={disabled} />;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useDivisionsSelectorStyles = makeStyles((theme, { disabled } : { disabled?: boolean }) => ({
    table: {
        width: "100%",
        borderSpacing: 0,
        wordBreak: "break-word",
        opacity: disabled ? 0.5 : undefined,
        cursor: disabled ? "not-allowed" : undefined
    }
}));

const DefaultRowRenderer: React.FCWithChildren = ({ children }) => <tr><td>{children}</td></tr>;

interface DivisionsSelectorDropdownProps {
    organisationDivisions: any[];
    array: {
        fields: any[];
        append: (value: any) => void;
        remove: (index: number) => void;
    };
    divisions: any[];
    fieldName?: string;
    divisionNameField?: string;
    divisionIdField?: string;
    divisionUuidField?: string;
    RowRenderer?: React.FCWithChildren;
    newDivisionMapper?: (division: any) => any;
    headers: { label?: string; width?: string }[];
    wrapHeader?: boolean;
    disabled?: boolean;
    control: Control;
    divisionsErrorPath?: string;
}

export const DivisionsSelectorDropdown: React.FC<DivisionsSelectorDropdownProps> = ({
    organisationDivisions,
    array,
    divisions,
    fieldName = "divisions",
    divisionNameField = "name",
    divisionIdField = "id",
    divisionUuidField = "uuid",
    RowRenderer = DefaultRowRenderer,
    newDivisionMapper,
    headers,
    wrapHeader,
    disabled,
    control,
    divisionsErrorPath
}) => {
    const classes = useDivisionsSelectorStyles({ disabled });

    return (
        <table className={classes.table} cellPadding={0} cellSpacing={0}>
            <thead>
                <tr>
                    {headers.map(({ label, ...rest }, i) => {
                        const wrap = wrapHeader && i === 0 ? "wrap" : "nowrap";
                        return (<Box key={i} component="td" whiteSpace={wrap} paddingBottom={0.5} {...rest}>
                            <Typography variant="label1" component="label"><T>{label}</T></Typography>
                        </Box>);
                    })}
                </tr>
            </thead>
            <tbody>
                {divisions && <Divisions
                    divisions={divisions}
                    fields={array.fields}
                    deleteDivision={array.remove}
                    divisionNameField={divisionNameField}
                    divisionIdField={divisionIdField}
                    divisionUuidField={divisionUuidField}
                    RowRenderer={RowRenderer}
                    disabled={disabled}
                />}

                {!disabled && <AddDivision
                    organisationDivisions={organisationDivisions}
                    divisions={divisions}
                    divisionNameField={divisionNameField}
                    divisionIdField={divisionIdField}
                    divisionUuidField={divisionUuidField}
                    array={array}
                    control={control}
                    fieldName={fieldName}
                    newDivisionMapper={newDivisionMapper}
                    divisionsErrorPath={divisionsErrorPath}
                />}
            </tbody>
        </table>
    );
};
